import React from 'react';
import { t } from '@core/i18n';
import type { BillingMetric } from './billingShared';
import {
  DashboardTable,
  DashboardTableCell,
  DashboardTableHeader,
  DashboardTableHeaderCell,
  DashboardTableIntro,
  DashboardTableRow,
  DashboardTableState,
  DashboardTableStatus,
  dashboardTableColumns,
  useDashboardTablePagination,
} from '../../components/DashboardTable';
import type { DashboardBillingAccountActivityEntry } from './consoleBillingApi';
import { formatUsdMinor } from './consoleBillingApi';

const BILLING_ACCOUNT_ACTIVITY_TABLE_COLUMNS = dashboardTableColumns(
  '1.2fr',
  '1.05fr',
  '0.8fr',
  '1.1fr',
  '1fr',
  '1.7fr',
);
export function formatTimestampUtcParts(value: string): {
  primary: string;
  secondary: string | null;
} {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return {
      primary: '-',
      secondary: null,
    };
  }
  const iso = date.toISOString();
  const [day, time = ''] = iso.split('T');
  return {
    primary: day || '-',
    secondary: `${time.replace('Z', '')} UTC`,
  };
}

export function describeAccountActivityType(input: DashboardBillingAccountActivityEntry): string {
  if (input.type === 'MANUAL_ADJUSTMENT') {
    return input.amountMinor >= 0
      ? t('billingActivityManualCredit')
      : t('billingActivityManualDebit');
  }
  if (input.type === 'PLAN_BASE_CHARGE') return t('billingActivityPlanBase');
  if (input.type === 'PLAN_USAGE_CHARGE')
    return input.amountMinor < 0
      ? t('billingActivityPlanUsageCharge')
      : t('billingActivityPlanUsageCredit');
  if (input.type === 'INVOICE_PAYMENT') return t('billingActivityInvoicePayment');
  if (input.type === 'CREDIT_PURCHASE') return t('billingActivityCreditPurchase');
  if (input.type === 'USAGE_DEBIT') return t('billingActivityUsageDebit');
  if (input.type === 'PRODUCT_EXECUTION_DEBIT') return t('billingActivityExecutionDebit');
  return input.type;
}

function getAccountActivityTone(input: DashboardBillingAccountActivityEntry) {
  if (
    input.type === 'PLAN_BASE_CHARGE' ||
    (input.type === 'PLAN_USAGE_CHARGE' && input.amountMinor < 0) ||
    input.type === 'USAGE_DEBIT' ||
    input.type === 'PRODUCT_EXECUTION_DEBIT' ||
    (input.type === 'MANUAL_ADJUSTMENT' && input.amountMinor < 0)
  ) {
    return 'warning' as const;
  }
  if (input.type === 'REFUND' || input.type === 'DISPUTE_OPENED' || input.type === 'DISPUTE_WON') {
    return 'neutral' as const;
  }
  return 'success' as const;
}

function getAccountActivityDocument(entry: DashboardBillingAccountActivityEntry): {
  id: string;
  label: string;
} | null {
  if (entry.relatedInvoiceId) {
    return { id: entry.relatedInvoiceId, label: t('billingActivityDocumentLabel') };
  }
  if (entry.relatedPurchaseId) {
    return { id: entry.relatedPurchaseId, label: t('billingActivityPurchaseLabel') };
  }
  return null;
}

function getAccountActivityReasonDetail(
  entry: DashboardBillingAccountActivityEntry,
): string | null {
  if (entry.actorUserId) return t('billingActivityActorUser', { id: entry.actorUserId });
  if (entry.actorType && entry.actorType !== 'SYSTEM')
    return t('billingActivityActorUser', { id: entry.actorType });
  if (entry.sourceEventId) return t('billingActivityEvent', { id: entry.sourceEventId });
  return null;
}

function getAccountActivitySummary(entry: DashboardBillingAccountActivityEntry): {
  primary: string;
  secondary: string | null;
} {
  const primary = entry.description || '-';
  const secondary =
    entry.note && entry.note !== entry.description
      ? entry.note
      : entry.sourceEventId && entry.sourceEventId !== entry.description
        ? entry.sourceEventId
        : null;
  return {
    primary,
    secondary,
  };
}

export function BillingContextSummarySection(props: {
  context: {
    organization: string;
    project: string;
    thirdLabel?: string;
    thirdValue?: string;
  };
  title: string;
  description: string;
  ariaLabel: string;
  metrics?: BillingMetric[];
}): React.JSX.Element {
  const { context, title, description, ariaLabel, metrics = [] } = props;
  return (
    <section className="dashboard-view__section dashboard-billing-overview" aria-label={ariaLabel}>
      <div className="dashboard-billing-overview__header">
        <div className="dashboard-billing-overview__copy">
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <dl className="dashboard-billing-overview__context">
        <div>
          <dt>{t('billingContextOrganization')}</dt>
          <dd title={context.organization || '-'}>{context.organization || '-'}</dd>
        </div>
        <div>
          <dt>{t('billingContextProject')}</dt>
          <dd title={context.project || '-'}>{context.project || '-'}</dd>
        </div>
        <div>
          <dt>{context.thirdLabel || t('billingContextEnvironment')}</dt>
          <dd title={context.thirdValue || '-'}>{context.thirdValue || '-'}</dd>
        </div>
      </dl>
      {metrics.length > 0 ? (
        <div className="dashboard-kpi-grid dashboard-kpi-grid--content dashboard-billing-overview__metrics">
          {metrics.map((metric) => (
            <article className="dashboard-kpi-card" key={metric.label}>
              <p className="dashboard-kpi-card__label">{metric.label}</p>
              <p className="dashboard-kpi-card__value">{metric.value}</p>
              <p className="dashboard-kpi-card__hint">{metric.hint}</p>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function BillingAccountActivitySection(props: {
  accountActivity: DashboardBillingAccountActivityEntry[];
  accountActivityError: string;
  controls?: React.ReactNode;
  emptyStateText?: string;
}): React.JSX.Element {
  const { accountActivity, accountActivityError, controls, emptyStateText } = props;
  const activityPagination = useDashboardTablePagination(accountActivity, {
    initialRowsPerPage: 10,
    itemLabel: t('billingItemEvent'),
    itemLabelPlural: t('billingItemEvents'),
  });
  return (
    <DashboardTable
      ariaLabel={t('billingCustomerActivityLabel')}
      className="dashboard-billing-activity-table"
      columns={BILLING_ACCOUNT_ACTIVITY_TABLE_COLUMNS}
      pagination={accountActivityError ? undefined : activityPagination.pagination}
    >
      <DashboardTableIntro className="dashboard-billing-table__intro">
        <div className="dashboard-billing-activity-table__heading">
          <h3 className="dashboard-billing-table__title">{t('billingCustomerActivityTitle')}</h3>
          <p className="dashboard-billing-table__description">
            {t('billingCustomerActivityIntro')}
          </p>
        </div>
        {controls}
      </DashboardTableIntro>
      {accountActivityError ? (
        <DashboardTableState>{accountActivityError}</DashboardTableState>
      ) : accountActivity.length === 0 ? (
        <DashboardTableState>{emptyStateText || t('billingNoActivity')}</DashboardTableState>
      ) : (
        <>
          <DashboardTableHeader>
            <DashboardTableHeaderCell>{t('billingColWhenUtc')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('billingColType')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('billingColAmount')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('billingColDocument')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('billingColReason')}</DashboardTableHeaderCell>
            <DashboardTableHeaderCell>{t('billingColSummary')}</DashboardTableHeaderCell>
          </DashboardTableHeader>
          {activityPagination.rows.map((entry) => {
            const document = getAccountActivityDocument(entry);
            const reasonDetail = getAccountActivityReasonDetail(entry);
            const summary = getAccountActivitySummary(entry);
            const createdAt = formatTimestampUtcParts(entry.createdAt);
            return (
              <DashboardTableRow key={entry.id}>
                <DashboardTableCell title={entry.createdAt}>
                  <div className="dashboard-billing-activity-table__stack">
                    <strong className="dashboard-data-table__summary dashboard-billing-activity-table__timestamp">
                      {createdAt.primary}
                    </strong>
                    {createdAt.secondary ? (
                      <span className="dashboard-data-table__subline dashboard-data-table__subline--muted dashboard-billing-activity-table__timestamp-detail">
                        {createdAt.secondary}
                      </span>
                    ) : null}
                  </div>
                  {entry.monthUtc ? (
                    <span className="dashboard-data-table__subline dashboard-data-table__subline--muted">
                      {t('billingActivityPeriod', { month: entry.monthUtc })}
                    </span>
                  ) : null}
                </DashboardTableCell>
                <DashboardTableCell>
                  <DashboardTableStatus tone={getAccountActivityTone(entry)}>
                    {describeAccountActivityType(entry)}
                  </DashboardTableStatus>
                </DashboardTableCell>
                <DashboardTableCell title={formatUsdMinor(entry.amountMinor)} align="end">
                  <strong className="dashboard-data-table__summary">
                    {formatUsdMinor(entry.amountMinor)}
                  </strong>
                </DashboardTableCell>
                <DashboardTableCell title={document?.id || undefined}>
                  {document ? (
                    <div className="dashboard-billing-activity-table__stack">
                      <code className="dashboard-billing-activity-table__token" title={document.id}>
                        {document.id}
                      </code>
                      <span className="dashboard-data-table__subline dashboard-data-table__subline--muted">
                        {document.label}
                      </span>
                    </div>
                  ) : (
                    '-'
                  )}
                </DashboardTableCell>
                <DashboardTableCell title={entry.reasonCode || reasonDetail || undefined}>
                  <div className="dashboard-billing-activity-table__stack">
                    <strong className="dashboard-data-table__summary">
                      {entry.reasonCode || '-'}
                    </strong>
                    {reasonDetail ? (
                      <span className="dashboard-data-table__subline dashboard-data-table__subline--muted dashboard-billing-activity-table__detail">
                        {reasonDetail}
                      </span>
                    ) : null}
                  </div>
                </DashboardTableCell>
                <DashboardTableCell title={summary.secondary || summary.primary}>
                  <div className="dashboard-billing-activity-table__stack">
                    <strong className="dashboard-data-table__summary">{summary.primary}</strong>
                    {summary.secondary ? (
                      <code
                        className="dashboard-billing-activity-table__token dashboard-billing-activity-table__token--muted"
                        title={summary.secondary}
                      >
                        {summary.secondary}
                      </code>
                    ) : null}
                  </div>
                </DashboardTableCell>
              </DashboardTableRow>
            );
          })}
        </>
      )}
    </DashboardTable>
  );
}
